// ============================================
// FILE: src/pages/PropertyDetailsPage.jsx
// ============================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { properties } from '../data/properties';
import {
  ArrowLeft, MapPin, BedDouble, Bath, Ruler, Compass, Building2,
  Car, CalendarCheck2, Sofa, Phone, MessageCircle, Check, Sparkles,
  ChevronLeft, ChevronRight, Home
} from 'lucide-react';




const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;1,700&family=Outfit:wght@300;400;500;600;700&display=swap');

  :root {
    --terra: #c8602a; --terra-light: #e8845a; --terra-pale: #fdf0e8;
    --cream: #faf7f2; --ink: #1a1208; --muted: #8a7968;
    --border: #e8e0d4; --white: #ffffff; --shadow-warm: rgba(200,96,42,0.15);
  }

  @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes imgFade { from { opacity: 0; } to { opacity: 1; } }

  .pd-page { font-family: 'Outfit', sans-serif; background: var(--cream); min-height: 100vh; }
  .pd-page h1, .pd-page h2, .pd-serif { font-family: 'Playfair Display', serif; }

  .pd-topbar {
    position: sticky; top: 0; z-index: 20;
    background: rgba(250,247,242,0.9); backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border);
    padding: 16px 24px;
  }
  .pd-topbar-inner { max-width: 1200px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .pd-back {
    display: inline-flex; align-items: center; gap: 8px;
    background: none; border: none; cursor: pointer;
    color: var(--ink); font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px;
    padding: 8px 4px; transition: color 0.2s, gap 0.2s;
  }
  .pd-back:hover { color: var(--terra); gap: 11px; }
  .pd-crumb { font-size: 13px; color: var(--muted); }
  .pd-crumb a { color: var(--muted); text-decoration: none; }
  .pd-crumb a:hover { color: var(--terra); }

  .pd-hero { max-width: 1200px; margin: 0 auto; padding: 28px 24px 0; }
  .pd-main-img-wrap {
    position: relative; width: 100%; height: 450px; border-radius: 22px; overflow: hidden;
    box-shadow: 0 20px 50px rgba(26,18,8,0.12);
  }
  .pd-main-img { width: 100%; height: 100%; object-fit: cover; animation: imgFade 0.35s ease; }
  .pd-nav-btn {
    position: absolute; top: 50%; transform: translateY(-50%);
    width: 44px; height: 44px; border-radius: 50%;
    background: rgba(255,255,255,0.9); border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--ink); box-shadow: 0 6px 18px rgba(0,0,0,0.18);
    transition: background 0.2s, transform 0.2s;
  }
  .pd-nav-btn:hover { background: #fff; transform: translateY(-50%) scale(1.08); }
  .pd-nav-prev { left: 18px; } .pd-nav-next { right: 18px; }
  .pd-counter {
    position: absolute; bottom: 16px; left: 16px;
    background: rgba(20,14,6,0.6); color: #fff; font-size: 12.5px; font-weight: 600;
    padding: 6px 14px; border-radius: 999px;
  }
  .pd-badge-row { position: absolute; top: 16px; left: 16px; display: flex; gap: 8px; }
  .pd-type-badge {
    background: var(--terra); color: #fff; font-size: 12px; font-weight: 700;
    padding: 6px 16px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.05em;
  }
  .pd-featured-badge {
    background: rgba(255,255,255,0.95); color: var(--terra); font-size: 12px; font-weight: 700;
    padding: 6px 14px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;
  }

  .pd-thumbs { display: flex; gap: 10px; margin-top: 14px; overflow-x: auto; padding-bottom: 4px; }
  .pd-thumb {
    flex: 0 0 auto; width: 110px; height: 78px; border-radius: 14px; overflow: hidden;
    cursor: pointer; border: 2.5px solid transparent; opacity: 0.55;
    transition: opacity 0.2s, border-color 0.2s; position: relative;
  }
  .pd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pd-thumb.active { opacity: 1; border-color: var(--terra); }
  .pd-thumb:hover { opacity: 1; }

  .pd-body { max-width: 1200px; margin: 0 auto; padding: 40px 24px 90px; display: grid; grid-template-columns: 1.6fr 1fr; gap: 44px; align-items: start; }
  @media (max-width: 900px) { .pd-body { grid-template-columns: 1fr; } }

  .pd-title-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; margin-bottom: 6px; }
  .pd-title { font-size: 34px; color: var(--ink); margin: 0 0 8px; line-height: 1.15; }
  .pd-location { display: flex; align-items: center; gap: 6px; color: var(--muted); font-size: 15px; margin-bottom: 22px; }
  .pd-price { font-size: 26px; color: var(--terra); white-space: nowrap; }

  .pd-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 32px; }
  .pd-stat { background: var(--white); border: 1px solid var(--border); border-radius: 16px; padding: 18px 14px; text-align: center; }
  .pd-stat svg { color: var(--terra); margin-bottom: 6px; }
  .pd-stat .val { font-size: 18px; font-weight: 700; color: var(--ink); }
  .pd-stat .lbl { font-size: 11.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }

  .pd-section-title { font-size: 13px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 14px; }
  .pd-desc { font-size: 15.5px; line-height: 1.85; color: #4a3f30; font-weight: 300; margin-bottom: 34px; }

  .pd-detail-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 14px 24px; margin-bottom: 34px; }
  .pd-detail-row {
    display: flex; align-items: center; gap: 10px; font-size: 14.5px; color: var(--ink);
    background: var(--white); border: 1px solid var(--border); border-radius: 12px; padding: 12px 14px;
  }
  .pd-detail-row svg { color: var(--terra); flex-shrink: 0; }
  .pd-detail-row .k { color: var(--muted); }

  .pd-amenities { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
  .pd-amenity {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--white); border: 1px solid var(--border);
    border-radius: 999px; padding: 8px 16px; font-size: 13.5px; color: var(--ink);
  }
  .pd-amenity svg { color: var(--terra); }

  .pd-sidebar { position: sticky; top: 90px; }
  .pd-contact-card {
    background: linear-gradient(135deg,#1a1208 0%,#2d1f0e 100%);
    border-radius: 22px; padding: 30px 26px; color: #fff;
    box-shadow: 0 20px 50px rgba(26,18,8,0.25);
  }
  .pd-contact-title { font-size: 20px; margin: 0 0 6px; }
  .pd-contact-sub { color: rgba(255,255,255,0.55); font-size: 13.5px; margin: 0 0 24px; }
  .pd-cta-primary, .pd-cta-secondary {
    width: 100%; display: flex; align-items: center; justify-content: center; gap: 9px;
    padding: 14px 18px; border-radius: 13px; font-weight: 700; font-size: 14.5px;
    cursor: pointer; border: none; font-family: 'Outfit', sans-serif; margin-bottom: 12px;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .pd-cta-primary { background: var(--terra); color: #fff; box-shadow: 0 10px 26px rgba(200,96,42,0.4); }
  .pd-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(200,96,42,0.5); }
  .pd-cta-secondary { background: rgba(255,255,255,0.08); color: #fff; border: 1.5px solid rgba(255,255,255,0.18); margin-bottom: 0; }
  .pd-cta-secondary:hover { border-color: var(--terra); background: rgba(200,96,42,0.15); }

  .pd-not-found { max-width: 600px; margin: 100px auto; text-align: center; padding: 0 24px; }
`;

const useScrollTop = (dep) => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' }); }, [dep]);
};

const PropertyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const property = properties.find(p => String(p.id) === String(id));
  const [index, setIndex] = useState(0);

  useScrollTop(id);
  useEffect(() => { setIndex(0); }, [id]);

  if (!property) {
    return (
      <div className="pd-page">
        <style>{STYLES}</style>
        <div className="pd-not-found">
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🏠</div>
          <h2 className="pd-serif" style={{ fontSize: '26px', color: 'var(--ink)', marginBottom: '10px' }}>Property Not Found</h2>
          <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>This listing may have been removed or the link is incorrect.</p>
          <button
            onClick={() => navigate('/rent')}
            style={{ background: 'var(--terra)', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 28px', fontFamily: 'Outfit,sans-serif', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}
          >
            Back to Listings
          </button>
        </div>
      </div>
    );
  }

  const images = property.images?.length ? property.images : [property.image];
  const next = () => setIndex(i => (i + 1) % images.length);
  const prev = () => setIndex(i => (i - 1 + images.length) % images.length);

  const specs = [
    property.furnishing && { icon: <Sofa size={16} />, k: 'Furnishing', v: property.furnishing },
    property.floor && { icon: <Building2 size={16} />, k: 'Floor', v: property.floor },
    property.facing && { icon: <Compass size={16} />, k: 'Facing', v: property.facing },
    property.parking && { icon: <Car size={16} />, k: 'Parking', v: property.parking },
    property.possession && { icon: <CalendarCheck2 size={16} />, k: 'Possession', v: property.possession },
  ].filter(Boolean);

  return (
    <div className="pd-page">
      <style>{STYLES}</style>

      {/* ── Top bar ── */}
      <div className="pd-topbar">
        <div className="pd-topbar-inner">
          <button className="pd-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={17} /> Back to Listings
          </button>
          <span className="pd-crumb">
            <Link to="/"><Home size={12} style={{ marginBottom: '-1px' }} /></Link> / <Link to="/rent">{property.type === 'Rent' ? 'Rent' : 'Buy'}</Link> / {property.title}
          </span>
        </div>
      </div>

      {/* ── Hero gallery ── */}
      <div className="pd-hero" style={{ animation: 'fadeIn 0.4s ease' }}>
        <div className="pd-main-img-wrap">
          <img key={index} className="pd-main-img" src={images[index]} alt={`${property.title} — photo ${index + 1}`} />
          {images.length > 1 && (
            <>
              <button className="pd-nav-btn pd-nav-prev" onClick={prev} aria-label="Previous photo"><ChevronLeft size={20} /></button>
              <button className="pd-nav-btn pd-nav-next" onClick={next} aria-label="Next photo"><ChevronRight size={20} /></button>
            </>
          )}
          <div className="pd-badge-row">
            <span className="pd-type-badge">{property.type === 'Rent' ? 'For Rent' : 'For Sale'}</span>
            {property.featured && <span className="pd-featured-badge"><Sparkles size={12} /> Featured</span>}
          </div>
          <span className="pd-counter">{index + 1} / {images.length}</span>
        </div>

        {images.length > 1 && (
          <div className="pd-thumbs">
            {images.map((img, i) => (
              <div key={i} className={`pd-thumb ${i === index ? 'active' : ''}`} onClick={() => setIndex(i)}>
                <img src={img} alt={`${property.title} — photo ${i + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="pd-body">
        {/* Left column */}
        <div style={{ animation: 'fadeSlideUp 0.5s ease 0.05s both' }}>
          <div className="pd-title-row">
            <div>
              <h1 className="pd-title">{property.title}</h1>
              <div className="pd-location"><MapPin size={15} color="var(--terra)" /> {property.location}</div>
            </div>
            <div className="pd-price pd-serif">{property.price}</div>
          </div>

          <div className="pd-stats">
            <div className="pd-stat">
              <BedDouble size={20} />
              <div className="val">{property.bedrooms > 0 ? property.bedrooms : '—'}</div>
              <div className="lbl">Bedrooms</div>
            </div>
            <div className="pd-stat">
              <Bath size={20} />
              <div className="val">{property.bathrooms}</div>
              <div className="lbl">Bathrooms</div>
            </div>
            <div className="pd-stat">
              <Ruler size={20} />
              <div className="val">{property.area?.replace(' sqft', '')}</div>
              <div className="lbl">Sqft Area</div>
            </div>
          </div>

          {property.description && (
            <>
              <div className="pd-section-title">About This Property</div>
              <p className="pd-desc">{property.description}</p>
            </>
          )}

          {specs.length > 0 && (
            <>
              <div className="pd-section-title">Property Details</div>
              <div className="pd-detail-grid">
                {specs.map((s, i) => (
                  <div className="pd-detail-row" key={i}>
                    {s.icon} <span className="k">{s.k}:</span> {s.v}
                  </div>
                ))}
              </div>
            </>
          )}

          {property.amenities?.length > 0 && (
            <>
              <div className="pd-section-title">Amenities</div>
              <div className="pd-amenities">
                {property.amenities.map((a, i) => (
                  <span className="pd-amenity" key={i}><Check size={13} /> {a}</span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right column — sticky contact card */}
        <div className="pd-sidebar" style={{ animation: 'fadeSlideUp 0.5s ease 0.15s both' }}>
          <div className="pd-contact-card">
            <h3 className="pd-contact-title pd-serif">Interested in this property?</h3>
            <p className="pd-contact-sub">Our team can arrange a visit or answer any questions you have.</p>
           <button 
  className="pd-cta-primary"
  onClick={() => navigate("/inquiry")}
>
  <Phone size={16} /> Contact Agent
</button>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailsPage;